import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TextScale = 100 | 115 | 130;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface GameSettings {
  masterVolume: number;
  musicVolume: number;
  effectsVolume: number;
  muted: boolean;
  reducedMotion: boolean;
  screenShake: boolean;
  highContrast: boolean;
  textScale: TextScale;
  oneHandedMode: boolean;
}

export interface LastWreckSaveV1 {
  version: 1;
  bestScore: number;
  bestCoreCondition: number;
  successfulRuns: number;
  tutorialComplete: boolean;
  settings: GameSettings;
}

export const DEFAULT_SETTINGS: GameSettings = {
  masterVolume: 0.85,
  musicVolume: 0.65,
  effectsVolume: 0.9,
  muted: false,
  reducedMotion: prefersReducedMotion(),
  screenShake: true,
  highContrast: false,
  textScale: 100,
  oneHandedMode: false,
};

export const DEFAULT_SAVE: LastWreckSaveV1 = {
  version: 1,
  bestScore: 0,
  bestCoreCondition: 0,
  successfulRuns: 0,
  tutorialComplete: false,
  settings: DEFAULT_SETTINGS,
};

const clampVolume = (value: unknown, fallback: number): number => {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : fallback;
};

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const asTextScale = (value: unknown): TextScale => (value === 115 || value === 130 ? value : 100);

/** Converts unknown persisted data into the only supported save version. */
export const migrateSave = (persisted: unknown): LastWreckSaveV1 => {
  if (typeof persisted !== 'object' || persisted === null) return DEFAULT_SAVE;

  const candidate = persisted as Record<string, unknown>;
  const rawSettings =
    typeof candidate.settings === 'object' && candidate.settings !== null
      ? (candidate.settings as Record<string, unknown>)
      : {};

  return {
    version: 1,
    bestScore:
      typeof candidate.bestScore === 'number' && candidate.bestScore >= 0
        ? Math.floor(candidate.bestScore)
        : 0,
    bestCoreCondition:
      typeof candidate.bestCoreCondition === 'number'
        ? Math.min(100, Math.max(0, candidate.bestCoreCondition))
        : 0,
    successfulRuns:
      typeof candidate.successfulRuns === 'number' && candidate.successfulRuns >= 0
        ? Math.floor(candidate.successfulRuns)
        : 0,
    tutorialComplete: asBoolean(candidate.tutorialComplete, false),
    settings: {
      masterVolume: clampVolume(rawSettings.masterVolume, DEFAULT_SETTINGS.masterVolume),
      musicVolume: clampVolume(rawSettings.musicVolume, DEFAULT_SETTINGS.musicVolume),
      effectsVolume: clampVolume(rawSettings.effectsVolume, DEFAULT_SETTINGS.effectsVolume),
      muted: asBoolean(rawSettings.muted, DEFAULT_SETTINGS.muted),
      reducedMotion: asBoolean(rawSettings.reducedMotion, DEFAULT_SETTINGS.reducedMotion),
      screenShake: asBoolean(rawSettings.screenShake, DEFAULT_SETTINGS.screenShake),
      highContrast: asBoolean(rawSettings.highContrast, DEFAULT_SETTINGS.highContrast),
      textScale: asTextScale(rawSettings.textScale),
      oneHandedMode: asBoolean(rawSettings.oneHandedMode, DEFAULT_SETTINGS.oneHandedMode),
    },
  };
};

interface SettingsState extends LastWreckSaveV1 {
  updateSettings: (patch: Partial<GameSettings>) => void;
  recordRun: (score: number, coreCondition: number, successful: boolean) => void;
  recordSuccessfulRun: (score: number, coreCondition: number) => void;
  completeTutorial: () => void;
  resetTutorial: () => void;
  resetSave: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...DEFAULT_SAVE,
      updateSettings: (patch) => set((state) => ({ settings: { ...state.settings, ...patch } })),
      recordRun: (score, coreCondition, successful) =>
        set((state) => ({
          bestScore: Math.max(state.bestScore, Math.max(0, Math.floor(score))),
          bestCoreCondition: successful
            ? Math.max(state.bestCoreCondition, Math.min(100, Math.max(0, coreCondition)))
            : state.bestCoreCondition,
          successfulRuns: state.successfulRuns + (successful ? 1 : 0),
        })),
      recordSuccessfulRun: (score, coreCondition) =>
        set((state) => ({
          bestScore: Math.max(state.bestScore, Math.max(0, Math.floor(score))),
          bestCoreCondition: Math.max(
            state.bestCoreCondition,
            Math.min(100, Math.max(0, coreCondition)),
          ),
          successfulRuns: state.successfulRuns + 1,
        })),
      completeTutorial: () => set({ tutorialComplete: true }),
      resetTutorial: () => set({ tutorialComplete: false }),
      resetSave: () => set({ ...DEFAULT_SAVE }),
    }),
    {
      name: 'the-last-wreck.save.v1',
      version: 1,
      partialize: (state) => ({
        version: state.version,
        bestScore: state.bestScore,
        bestCoreCondition: state.bestCoreCondition,
        successfulRuns: state.successfulRuns,
        tutorialComplete: state.tutorialComplete,
        settings: state.settings,
      }),
      migrate: (persisted) => migrateSave(persisted),
    },
  ),
);
